import { ErrorBoundary } from 'components/Errors/ErrorBoundary';
import LoadingScreen from 'components/LoadingScreen';
import ModalDialog from 'components/ModalDialog';
import PolicyFilterDropdown from 'components/PolicyFilterDropdown';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import React, { Fragment, Suspense, useEffect, useRef, useState } from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Row,
  Col,
  Collapse,
  Button,
} from 'reactstrap';

import AssetsList from './AssetListing';
import ResetDashboardButton from './AssetListing/ResetDashboardButton';
import CurrentCharts from './currentCharts';
import HistoryCharts from './historyCharts';
import {
  criticalityFiltersAtom,
  timeSpanAtom,
  intervalAtom,
  showHistoryChartCountsAtom,
  historyChartsOpenAtom,
  currentVulnChartsOpenAtom,
  isGroupingByQidVulnListingOpenAtom,
  policyIdFilterAtom,
  appliedHostIdsAtom,
} from './state';
import HistoryChartHeader from '../components/HistoryChart/HistoryChartHeader';
import '../styles.css';
import SeverityFilterDropdown from '../components/SeverityFilterDropdown';
import { DashboardType, ModalDialogData } from '../types';

const PCDashboard = () => {
  const setIsGroupingByQidVulnListingOpen = useSetAtom(
    isGroupingByQidVulnListingOpenAtom,
  );
  const [policyId, setPolicyId] = useAtom(policyIdFilterAtom);
  const appliedHostIds = useAtomValue(appliedHostIdsAtom);

  // TOGGLE THE COLLAPSE FOR HISTORY CHARTS AND CURRENT CHARTS SECTIONS
  const [historyChartsOpen, setHistoryChartsOpen] = useAtom(
    historyChartsOpenAtom,
  );
  const [currentVulnChartsOpen, setCurrentVulnChartsOpen] = useAtom(
    currentVulnChartsOpenAtom,
  );

  const [modalComponentData, setModalComponentData] =
    useState<ModalDialogData>();

  const listingScrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (window.location.href.includes(`#${DashboardType.PC}`)) {
      setTimeout(() => {
        if (listingScrollRef.current) {
          listingScrollRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 1000);
    }
  });

  return (
    <Fragment>
      <div className="container-fluid p-0">
        <nav
          className="navbar navbar-expand-sm bg-white border-top-0 border border-bottom-light w-100 sticky-top"
          style={{ padding: '0.1rem 0px' }}
        >
          <div
            className="row w-100 align-items-start align-items-center"
            style={{ margin: '0px' }}
          >
            {/* First Column: Left-aligned content */}
            <Col xs="12" md="3" lg="6">
              {appliedHostIds.length > 0 && (
                <>
                  <i className="fa fa-info-circle me-1 text-warning px-2"></i>
                  Showing dashboard for selected{' '}
                  <strong>{appliedHostIds.length}</strong> hosts only.&nbsp;
                  <ResetDashboardButton />
                </>
              )}
            </Col>

            {/* Second Column: Right-aligned, wrapping items */}
            <Col
              xs="12"
              md="9"
              lg="6"
              className="d-flex col-auto p-0 justify-content-end"
            >
              <div className="d-flex flex-wrap w-100">
                <div className="py-1 px-2 flex-grow-1">
                  <PolicyFilterDropdown
                    selectedPolicyId={policyId}
                    for_hosts={appliedHostIds}
                    onChange={(value) => setPolicyId(value)}
                  />
                </div>
                <div className="py-1 px-2 flex-grow-1">
                  <SeverityFilterDropdown
                    severityFiltersAtom={criticalityFiltersAtom}
                    placeholderText="Criticality"
                  />
                </div>
              </div>
            </Col>
          </div>
        </nav>
        <Card>
          <CardHeader>
            <Row>
              <Col className="col-auto">
                <CardTitle tag="h4">
                  Compliance Trending History&nbsp;
                  <Button
                    outline={true}
                    color="primary"
                    size="sm"
                    onClick={() => setHistoryChartsOpen(!historyChartsOpen)}
                  >
                    <i
                      className={
                        historyChartsOpen
                          ? 'bi bi-chevron-up'
                          : 'bi bi-chevron-down'
                      }
                    ></i>
                  </Button>
                </CardTitle>
              </Col>
              <Col>
                <Collapse isOpen={historyChartsOpen}>
                  <div className="d-inline float-right">
                    <HistoryChartHeader
                      intervalAtom={intervalAtom}
                      showHistoryChartCountsAtom={showHistoryChartCountsAtom}
                      timeSpanAtom={timeSpanAtom}
                    />
                  </div>
                </Collapse>
              </Col>
            </Row>
          </CardHeader>
          <Collapse isOpen={historyChartsOpen}>
            <CardBody>
              <ErrorBoundary>
                <Suspense fallback={<LoadingScreen />}>
                  <HistoryCharts
                    setModalComponentData={setModalComponentData}
                  />
                </Suspense>
              </ErrorBoundary>
            </CardBody>
          </Collapse>
        </Card>
        <Card>
          <CardHeader>
            <Row>
              <Col>
                <CardTitle tag="h4">
                  Current Findings Status&nbsp;
                  <Button
                    outline={true}
                    color="primary"
                    size="sm"
                    onClick={() =>
                      setCurrentVulnChartsOpen(!currentVulnChartsOpen)
                    }
                  >
                    <i
                      className={
                        currentVulnChartsOpen
                          ? 'bi bi-chevron-up'
                          : 'bi bi-chevron-down'
                      }
                    ></i>
                  </Button>
                </CardTitle>
              </Col>
            </Row>
          </CardHeader>
          <Collapse isOpen={currentVulnChartsOpen}>
            <CardBody>
              <div className="row">
                <Suspense fallback={<LoadingScreen />}>
                  <CurrentCharts />
                </Suspense>
              </div>
            </CardBody>
          </Collapse>
        </Card>
      </div>
      <div ref={listingScrollRef} aria-hidden></div>
      <ErrorBoundary>
        {/* <PCList setModalComponentData={setModalComponentData} /> */}
        <AssetsList />
      </ErrorBoundary>
      <ModalDialog
        title={modalComponentData?.title}
        subTitle={modalComponentData?.subTitle}
        childComponent={modalComponentData?.bodyComponent}
        onClose={() => {
          setModalComponentData(undefined);
          setIsGroupingByQidVulnListingOpen(false);
        }}
      />
    </Fragment>
  );
};

export default PCDashboard;
